import KogCheckbox from '@/main/components/shared/checkbox/kogCheckbox';
import KOGImage from '@/main/components/shared/kogImage/kogImage';
import QuoteProcurementPartInfo from '@/main/components/shared/quoteProcurementPartInfo/quoteProcurementPartInfo';
import {
  IQuoteSupplierItems,
  QuoteGetItemsResponse
} from '@/main/models/response';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import * as S from './styles';

export interface IBasePartsForm {
  quoteSupplierItemGuids: string[];
}

interface ISupplierPartsSelectorProps {
  data: QuoteGetItemsResponse[];
}

export function SupplierPartsSelector({ data }: ISupplierPartsSelectorProps) {
  return (
    <S.AreaSupplier>
      {data.map(supplier => (
        <RowSupplier key={supplier.guidQuoteSupplier} supplier={supplier} />
      ))}
    </S.AreaSupplier>
  );
}

interface IRowSupplierProps {
  supplier: QuoteGetItemsResponse;
}

function RowSupplier({ supplier }: IRowSupplierProps) {
  const { watch, setValue } = useFormContext<IBasePartsForm>();
  const selectedGuids = watch('quoteSupplierItemGuids') || [];

  const allSupplierItemGuids = useMemo(() => {
    return supplier.quoteSupplierItems.map(item => item.guid);
  }, [supplier.quoteSupplierItems]);

  const allCheckboxesSelected = useMemo(() => {
    if (allSupplierItemGuids.length === 0) return false;
    return allSupplierItemGuids.every(guid => selectedGuids.includes(guid));
  }, [selectedGuids, allSupplierItemGuids]);

  function handleToggleCheckboxAll() {
    if (allCheckboxesSelected) {
      const newSelection = selectedGuids.filter(
        guid => !allSupplierItemGuids.includes(guid)
      );
      setValue('quoteSupplierItemGuids', newSelection);
    } else {
      const guidsToAdd = allSupplierItemGuids.filter(
        guid => !selectedGuids.includes(guid)
      );
      setValue('quoteSupplierItemGuids', [...selectedGuids, ...guidsToAdd]);
    }
  }

  return (
    <S.SupplierContainer>
      <S.HeaderSupplier onClick={handleToggleCheckboxAll}>
        <S.TitleSupplier>{supplier.companyName}</S.TitleSupplier>
        <KogCheckbox
          checked={allCheckboxesSelected}
          value={allCheckboxesSelected}
          onChange={handleToggleCheckboxAll}
        />
      </S.HeaderSupplier>
      <S.BodySupplier>
        {supplier.quoteSupplierItems.map(item => (
          <RowItem key={item.guid} part={item} />
        ))}
      </S.BodySupplier>
    </S.SupplierContainer>
  );
}

interface IRowItemProps {
  part: IQuoteSupplierItems;
}

function RowItem({ part }: IRowItemProps) {
  const { watch, setValue } = useFormContext<IBasePartsForm>();
  const selectedGuids = watch('quoteSupplierItemGuids') || [];
  const isSelected = selectedGuids.includes(part.guid);

  function handleToggleItem() {
    if (isSelected) {
      setValue(
        'quoteSupplierItemGuids',
        selectedGuids.filter(guid => guid !== part.guid)
      );
    } else {
      setValue('quoteSupplierItemGuids', [...selectedGuids, part.guid]);
    }
  }

  return (
    <S.ItemContainer onClick={handleToggleItem}>
      <KOGImage src={part.quoteItem.imageUrl} alt="" />
      <S.AreaTitlePart>
        <QuoteProcurementPartInfo
          data={part.quoteItem.quoteProcurementPartInfo}
          renderType="column"
        />
      </S.AreaTitlePart>
      <S.Quantity>{part.quantity}</S.Quantity>
      <KogCheckbox
        checked={isSelected}
        value={isSelected}
        onChange={handleToggleItem}
      />
    </S.ItemContainer>
  );
}
